import React, { useState } from 'react'
import { Card, Tabs, Form, Input, Button, Switch, Select, Space, Typography, message, Divider, List, Tag, Modal } from 'antd'
import {
  UserOutlined,
  SettingOutlined,
  BellOutlined,
  SafetyCertificateOutlined,
  ApiOutlined,
  SaveOutlined,
} from '@ant-design/icons'

const { Title, Text } = Typography
const { TabPane } = Tabs
const { Option } = Select

const Settings: React.FC = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)

  const handleSave = () => {
    setLoading(true)
    setTimeout(() => {
      message.success('设置保存成功')
      setLoading(false)
    }, 1000)
  }

  const userSettings = (
    <Form
      form={form}
      layout="vertical"
      initialValues={{
        name: '管理员',
        email: 'admin@fullhaul.com',
        role: 'admin',
        language: 'zh-CN',
        timezone: 'Asia/Shanghai',
      }}
    >
      <Title level={5}>个人信息</Title>
      <Form.Item
        name="name"
        label="用户名"
        rules={[{ required: true, message: '请输入用户名' }]}
      >
        <Input prefix={<UserOutlined />} placeholder="请输入用户名" />
      </Form.Item>
      <Form.Item
        name="email"
        label="邮箱"
        rules={[
          { required: true, message: '请输入邮箱' },
          { type: 'email', message: '请输入有效的邮箱地址' },
        ]}
      >
        <Input placeholder="请输入邮箱" />
      </Form.Item>
      <Form.Item
        name="role"
        label="角色"
      >
        <Select disabled>
          <Option value="admin">管理员</Option>
          <Option value="manager">经理</Option>
          <Option value="user">普通用户</Option>
        </Select>
      </Form.Item>
      <Form.Item
        name="language"
        label="语言"
      >
        <Select>
          <Option value="zh-CN">简体中文</Option>
          <Option value="en-US">English</Option>
        </Select>
      </Form.Item>
      <Form.Item
        name="timezone"
        label="时区"
      >
        <Select>
          <Option value="Asia/Shanghai">Asia/Shanghai (UTC+8)</Option>
          <Option value="America/New_York">America/New_York (UTC-5)</Option>
          <Option value="Europe/London">Europe/London (UTC+0)</Option>
        </Select>
      </Form.Item>
      <Form.Item>
        <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>
          保存设置
        </Button>
      </Form.Item>
    </Form>
  )

  const notificationSettings = (
    <Form
      layout="vertical"
      initialValues={{
        email_notification: true,
        push_notification: true,
        task_reminder: true,
        weekly_report: true,
        slack_webhook: '',
      }}
    >
      <Title level={5}>通知设置</Title>
      <Form.Item
        name="email_notification"
        label="邮件通知"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      <Form.Item
        name="push_notification"
        label="推送通知"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      <Form.Item
        name="task_reminder"
        label="任务提醒"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      <Form.Item
        name="weekly_report"
        label="周报"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      <Divider />
      <Title level={5}>Slack 集成</Title>
      <Form.Item
        name="slack_webhook"
        label="Slack Webhook URL"
      >
        <Input placeholder="请输入 Slack Webhook URL" />
      </Form.Item>
      <Form.Item>
        <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>
          保存设置
        </Button>
      </Form.Item>
    </Form>
  )

  const platformSettings = (
    <div>
      <Title level={5}>平台配置</Title>
      <List
        itemLayout="horizontal"
        dataSource={[
          {
            name: 'Amazon Haul',
            status: 'active',
            last_sync: '2026-08-31 10:30:00',
          },
          {
            name: 'Walmart',
            status: 'active',
            last_sync: '2026-08-31 10:25:00',
          },
          {
            name: 'Joom',
            status: 'active',
            last_sync: '2026-08-31 10:20:00',
          },
          {
            name: 'Ozon',
            status: 'active',
            last_sync: '2026-08-31 10:15:00',
          },
          {
            name: 'Shopify',
            status: 'active',
            last_sync: '2026-08-31 10:10:00',
          },
        ]}
        renderItem={(item) => (
          <List.Item
            actions={[
              <Button type="link" icon={<ApiOutlined />} onClick={() => message.info("打开平台配置")}>配置</Button>,
              <Button type="link" onClick={() => message.success("连接测试成功")}>测试连接</Button>,
            ]}
          >
            <List.Item.Meta
              title={item.name}
              description={
                <Space>
                  <Tag color={item.status === 'active' ? 'success' : 'default'}>
                    {item.status === 'active' ? '运行中' : '已停用'}
                  </Tag>
                  <Text type="secondary">最后同步: {item.last_sync}</Text>
                </Space>
              }
            />
          </List.Item>
        )}
      />
    </div>
  )

  const securitySettings = (
    <Form layout="vertical">
      <Title level={5}>安全设置</Title>
      <Form.Item
        name="current_password"
        label="当前密码"
        rules={[{ required: true, message: '请输入当前密码' }]}
      >
        <Input.Password placeholder="请输入当前密码" />
      </Form.Item>
      <Form.Item
        name="new_password"
        label="新密码"
        rules={[
          { required: true, message: '请输入新密码' },
          { min: 8, message: '密码长度至少为8位' },
        ]}
      >
        <Input.Password placeholder="请输入新密码" />
      </Form.Item>
      <Form.Item
        name="confirm_password"
        label="确认新密码"
        dependencies={['new_password']}
        rules={[
          { required: true, message: '请确认新密码' },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('new_password') === value) {
                return Promise.resolve()
              }
              return Promise.reject(new Error('两次输入的密码不一致'))
            },
          }),
        ]}
      >
        <Input.Password placeholder="请确认新密码" />
      </Form.Item>
      <Form.Item>
        <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSave}>
          修改密码
        </Button>
      </Form.Item>
      <Divider />
      <Title level={5}>双因素认证</Title>
      <Form.Item
        name="two_factor"
        label="启用双因素认证"
        valuePropName="checked"
      >
        <Switch />
      </Form.Item>
      <Text type="secondary">
        启用双因素认证后，登录时需要输入验证码，提高账户安全性。
      </Text>
    </Form>
  )

  return (
    <div>
      <Title level={4} style={{ marginBottom: 24 }}>
        系统设置
      </Title>

      <Card>
        <Tabs defaultActiveKey="user" tabPosition="left">
          <TabPane
            tab={
              <span>
                <UserOutlined />
                个人设置
              </span>
            }
            key="user"
          >
            {userSettings}
          </TabPane>
          <TabPane
            tab={
              <span>
                <BellOutlined />
                通知设置
              </span>
            }
            key="notification"
          >
            {notificationSettings}
          </TabPane>
          <TabPane
            tab={
              <span>
                <SettingOutlined />
                平台设置
              </span>
            }
            key="platform"
          >
            {platformSettings}
          </TabPane>
          <TabPane
            tab={
              <span>
                <SafetyCertificateOutlined />
                安全设置
              </span>
            }
            key="security"
          >
            {securitySettings}
          </TabPane>
        </Tabs>
      </Card>
    </div>
  )
}

export default Settings
